/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { format } from 'date-fns';

import {
    NoMessagesContainer,
    StyledChatBodyContentNew,
    StyledChatBodyHeader,
    StyledChatBodyHeaderTitle,
    StyledChatBodyNew,
} from './ChatBodyNew.styled';

import MessageItem from './components/MessageItem/MessageItem';
import ChatFooterNew from '../ChatFooterNew/ChatFooterNew';

import api from 'api';
import { StatusesEnum } from '../../../Dashboards-B2B/components/chatComponents/MessageItem/MessageStatuses';
import { useTranslation } from 'react-i18next';

export enum Sender {
    ME = 'ME',
    OTHER = 'OTHER',
    ADMIN = 'ADMIN',
}

interface ChatBodyNewProps {
    chatGroupId: string;
    chatTitle: string;
    handleChatClose: () => void;
}

export default function ChatBodyNew({ chatGroupId, chatTitle, handleChatClose }: ChatBodyNewProps) {
    const chatBodyContentRef = useRef();
    const footerRef = useRef();
    const bodyRef = useRef();
    const [contentHeight, setContentHeight] = useState('100%');
    const [footerHeight, setFooterHeight] = useState(80);
    const [messages, setMessages] = useState<any[]>([]);
    const room = chatGroupId;
    const user_id = useSelector((state: RootState) => state.user.entity.id);
    const [chatUserIds] = useState<number[]>([]);

    useEffect(() => {
        const getChatMessages = async () => {
            try {
                const res = await api.chatNew.get_chat_messages(chatGroupId);
                const receivedMessages = res.data.map((message: any) => {
                    let sender;

                    if (message.user_id === null) {
                        sender = Sender.ADMIN;
                    } else if (message.user_id !== user_id) {
                        sender = Sender.OTHER;
                    } else {
                        sender = Sender.ME;
                    }

                    return {
                        id: message.id,
                        createdAt: message.created_at,
                        sender: sender,
                        message: message.text,
                    };
                });

                setMessages((prevMessages) => {
                    const messageIds = prevMessages.map((message) => message.id);
                    const uniqueMessages = receivedMessages.filter(
                        (message: any) => !messageIds.includes(message.id)
                    );
                    return [...prevMessages, ...uniqueMessages];
                });
            } catch (error) {
                console.error(error);
            }
        };

        if (chatGroupId) {
            setMessages([]);
            getChatMessages();
        }
    }, [chatGroupId, user_id]);

    useEffect(() => {
        if (!footerRef?.current) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setFooterHeight(footerRef?.current?.clientHeight);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
    }, [footerRef?.current?.innerHTML]);

    useEffect(() => {
        if (!bodyRef?.current) {
            return;
        }

        const chaBodyHeaderHeight = 52;
        const mobileMargin = isMobile ? chaBodyHeaderHeight : 0;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const contentHeightCalculation =
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            bodyRef?.current?.clientHeight - footerHeight - mobileMargin;

        setContentHeight(() => `${contentHeightCalculation}px`);
    }, [footerHeight]);

    useEffect(() => {
        const chatBodyContent = chatBodyContentRef?.current;
        const body = bodyRef?.current;

        if (!chatBodyContent || !contentHeight || !body || messages.length < 1) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        chatBodyContentRef.current?.lastElementChild?.scrollIntoView();
    }, [contentHeight, messages]);

    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);

    const { t } = useTranslation();
    return (
        <>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/*// @ts-ignore*/}
            <StyledChatBodyNew ref={bodyRef}>
                {isMobile && (
                    <StyledChatBodyHeader>
                        <ArrowBackRoundedIcon onClick={handleChatClose} />
                        <StyledChatBodyHeaderTitle>{chatTitle}</StyledChatBodyHeaderTitle>
                        <div></div>
                    </StyledChatBodyHeader>
                )}
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/*// @ts-ignore*/}
                <StyledChatBodyContentNew heightValue={contentHeight} ref={chatBodyContentRef}>
                    {messages.length > 1 &&
                        messages.map((message, index) => {
                            const currentItemDate = format(new Date(message.createdAt), 'MMM d y');
                            const previousItemDate =
                                index === 0
                                    ? format(new Date(message.createdAt), 'MMM d y')
                                    : format(new Date(messages[index - 1]?.createdAt), 'MMM d y');

                            const showItemDate =
                                messages.length < 2
                                    ? false
                                    : index === 0
                                      ? true
                                      : currentItemDate !== previousItemDate;

                            return (
                                <MessageItem
                                    showItemDate={showItemDate}
                                    message={message.message}
                                    time={message.createdAt}
                                    sender={message.sender}
                                    key={index}
                                    author={message.sender === Sender.ME ? 'You' : message.user}
                                    status={
                                        message.read ? StatusesEnum.READ : StatusesEnum.DELIVERED
                                    }
                                />
                            );
                        })}
                    {messages.length <= 1 && (
                        <NoMessagesContainer>
                            <div>{t('noMessagesYet', { lng: language })}</div>
                        </NoMessagesContainer>
                    )}
                </StyledChatBodyContentNew>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/*// @ts-ignore*/}
                <ChatFooterNew
                    ref={footerRef}
                    setMessages={setMessages}
                    room={room}
                    chatUserIds={chatUserIds}
                />
            </StyledChatBodyNew>
        </>
    );
}
